import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createThread, getThreads } from "../../../../api/chat";
import { formatUserNowTime } from "../utils/chatHelpers";
import { useCompanyContext } from "../../../../context";

const THREAD_FETCH_COOLDOWN_MS = 500;

const makeDedupedFetcher = (fetchFn) => {
  const state = { inFlight: null, lastCompletedAt: 0 };

  return () => {
    if (state.inFlight) return state.inFlight;

    if (Date.now() - state.lastCompletedAt < THREAD_FETCH_COOLDOWN_MS) {
      return Promise.resolve();
    }

    state.inFlight = fetchFn().finally(() => {
      state.lastCompletedAt = Date.now();
      state.inFlight = null;
    });

    return state.inFlight;
  };
};

const getDraftRegistry = () => {
  if (!window.__chatDraftThreads) {
    window.__chatDraftThreads = {};
  }

  return window.__chatDraftThreads;
};

const isDraftRegistered = (threadId) => {
  if (!threadId) return false;
  return !!getDraftRegistry()[threadId];
};

const useChatThreads = ({
  threadType = "private",
  selectedModel = "",
} = {}) => {
  const [threads, setThreads] = useState([]);
  const [localThreads, setLocalThreads] = useState([]);
  const [currentThreadId, _setCurrentThreadId] = useState(null);
  const [persistedThreadIds, setPersistedThreadIds] = useState([]);

  const { companyKey } = useCompanyContext();

  const setCurrentThreadId = useCallback((value) => {
    _setCurrentThreadId(value);
  }, []);

  const localThreadIds = useMemo(
    () => localThreads.map((thread) => thread.id),
    [localThreads],
  );

  const localThreadIdsRef = useRef(localThreadIds);
  const localThreadsRef = useRef(localThreads);
  const persistedThreadIdsRef = useRef(persistedThreadIds);

  useEffect(() => {
    localThreadIdsRef.current = localThreadIds;
  }, [localThreadIds]);

  useEffect(() => {
    localThreadsRef.current = localThreads;
  }, [localThreads]);

  useEffect(() => {
    persistedThreadIdsRef.current = persistedThreadIds;
  }, [persistedThreadIds]);

  const allThreads = useMemo(() => {
    const serverMapped = threads
      .map((thread) => ({
        id: thread.thread_id,
        label: thread.title || "Thread",
        thread_type: thread.thread_type || threadType,
        isLocal: false,
        loading: false,
        status: "ready",
      }))
      .filter((thread) => thread.id != null);

    return [
      ...localThreads,
      ...serverMapped.filter(
        (serverThread) =>
          !localThreads.some(
            (localThread) => localThread.id === serverThread.id,
          ),
      ),
    ];
  }, [threads, localThreads, threadType]);

  const loadThreadsImpl = useCallback(async () => {
    try {
      const res = await getThreads(0, threadType);
      const raw = res?.data?.threads || [];
      setThreads(raw);
    } catch (error) {
      console.error("Error loading threads:", error);
      setThreads([]);
    }
  }, [threadType]);

  const loadThreadsRef = useRef(null);

  if (!loadThreadsRef.current) {
    loadThreadsRef.current = makeDedupedFetcher(loadThreadsImpl);
  }

  useEffect(() => {
    loadThreadsRef.current = makeDedupedFetcher(loadThreadsImpl);
  }, [loadThreadsImpl]);

  const loadThreads = useCallback(() => loadThreadsRef.current(), []);

  const upsertLocalDraft = useCallback(
    ({ threadId, thread_type = threadType, label = "New thread" }) => {
      if (!threadId || thread_type !== threadType) return threadId;

      getDraftRegistry()[threadId] = {
        id: threadId,
        thread_type,
        label,
      };

      setLocalThreads((prev) => {
        const existing = prev.find(
          (thread) => thread.isLocal && thread.thread_type === thread_type,
        );

        if (existing) {
          return prev.map((thread) =>
            thread.id === existing.id
              ? {
                  ...thread,
                  id: threadId,
                  label: thread.label || label,
                  thread_type,
                  isLocal: true,
                  status: thread.status || "draft",
                  loading: false,
                }
              : thread,
          );
        }

        return [
          {
            id: threadId,
            label,
            isLocal: true,
            loading: false,
            status: "draft",
            thread_type,
          },
          ...prev,
        ];
      });

      return threadId;
    },
    [threadType],
  );

  const createNewThread = useCallback(
    (preferredId = null) => {
      const existing = localThreadsRef.current.find(
        (thread) => thread.isLocal && thread.thread_type === threadType,
      );

      if (existing) {
        setCurrentThreadId(existing.id);
        return existing.id;
      }

      const id = preferredId || crypto.randomUUID();

      getDraftRegistry()[id] = {
        id,
        thread_type: threadType,
        label: "New thread",
      };

      setLocalThreads((prev) => [
        {
          id,
          label: "New thread",
          isLocal: true,
          loading: false,
          status: "draft",
          thread_type: threadType,
        },
        ...prev,
      ]);

      setCurrentThreadId(id);
      return id;
    },
    [setCurrentThreadId, threadType],
  );

  const markThreadPersisted = useCallback((threadId) => {
    if (!threadId) return;

    delete getDraftRegistry()[threadId];

    setLocalThreads((prev) => prev.filter((thread) => thread.id !== threadId));

    setPersistedThreadIds((prev) =>
      prev.includes(threadId) ? prev : [...prev, threadId],
    );

    window.dispatchEvent(
      new CustomEvent("chat:thread-persisted", {
        detail: { threadId },
      }),
    );
  }, []);

  const isLocalThread = useCallback(
    (threadId) =>
      localThreadIdsRef.current.includes(threadId) ||
      isDraftRegistered(threadId),
    [],
  );

  const ensureThreadExists = useCallback(
    async (threadId, firstMessage, threadAge) => {
      if (!threadId) return;

      if (persistedThreadIdsRef.current.includes(threadId)) return;

      const knownLocal =
        localThreadIdsRef.current.includes(threadId) ||
        isDraftRegistered(threadId);

      if (!knownLocal) {
        setPersistedThreadIds((prev) =>
          prev.includes(threadId) ? prev : [...prev, threadId],
        );
        return;
      }

      try {
        setLocalThreads((prev) =>
          prev.map((thread) =>
            thread.id === threadId
              ? { ...thread, loading: true, status: "creating" }
              : thread,
          ),
        );

        const res =
          threadType === "public"
            ? await createThread(
                threadId,
                firstMessage,
                threadAge,
                formatUserNowTime(),
                threadType,
                selectedModel,
              )
            : await createThread(
                threadId,
                firstMessage,
                threadAge,
                formatUserNowTime(),
                threadType,
              );

        const title = res?.data?.thread_data?.title || "New thread";

        setLocalThreads((prev) =>
          prev.map((thread) =>
            thread.id === threadId
              ? {
                  ...thread,
                  label: title,
                  loading: false,
                  status: "ready",
                }
              : thread,
          ),
        );

        window.dispatchEvent(
          new CustomEvent("chat:thread-title-updated", {
            detail: {
              threadId,
              title,
              thread_type: threadType,
            },
          }),
        );

        setPersistedThreadIds((prev) =>
          prev.includes(threadId) ? prev : [...prev, threadId],
        );

        await loadThreads();
        markThreadPersisted(threadId);

        window.dispatchEvent(new CustomEvent("sidebar:refresh-threads"));
      } catch (error) {
        console.error("ensureThreadExists failed:", error);

        setLocalThreads((prev) =>
          prev.map((thread) =>
            thread.id === threadId
              ? { ...thread, loading: false, status: "error" }
              : thread,
          ),
        );
      }
    },
    [loadThreads, markThreadPersisted, selectedModel, threadType],
  );

  useEffect(() => {
    const onDraftCreated = (event) => {
      const detail = event.detail || {};

      upsertLocalDraft({
        threadId: detail.threadId,
        thread_type: detail.thread_type,
        label: detail.label || "New thread",
      });
    };

    window.addEventListener("chat:draft-thread-created", onDraftCreated);

    return () => {
      window.removeEventListener("chat:draft-thread-created", onDraftCreated);
    };
  }, [upsertLocalDraft]);

  useEffect(() => {
    const onDiscardDraft = (event) => {
      const { threadId, thread_type } = event.detail || {};

      if (!threadId || thread_type !== threadType) return;

      delete getDraftRegistry()[threadId];

      setLocalThreads((prev) =>
        prev.filter((thread) => thread.id !== threadId),
      );

      if (currentThreadId === threadId) {
        setCurrentThreadId(null);
      }
    };

    window.addEventListener("chat:discard-draft-thread", onDiscardDraft);

    return () => {
      window.removeEventListener("chat:discard-draft-thread", onDiscardDraft);
    };
  }, [currentThreadId, setCurrentThreadId, threadType]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads, companyKey]);

  return {
    allThreads,
    currentThreadId,
    setCurrentThreadId,
    localThreadIds,
    loadThreads,
    createNewThread,
    markThreadPersisted,
    ensureThreadExists,
    isLocalThread,
  };
};

export default useChatThreads;
